import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, createContext, useContext, useCallback, useMemo, } from "react";
import { COMPONENT_OUTLINE_BORDER } from "../theme/componentBorders";
import { Card, Space, Button, Tooltip, Select } from "antd";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
// 创建历史记录上下文
const HistoryContext = createContext(null);
// 导出 hook，供子组件使用
export const useContainerPanelHistory = () => {
    const context = useContext(HistoryContext);
    if (!context) {
        throw new Error("useContainerPanelHistory must be used within a ContainerPanel with showHistory enabled");
    }
    return context;
};
/**
 * 通用面板容器组件
 * 支持标题或标签页，支持右侧按钮分组，支持边框和阴影
 */
const ContainerPanel = ({ title, tabs, activeTabKey, onTabChange, buttons = [], extraLeading, children, height, contentPadding = 16, bordered = true, shadow = false, cardProps = {}, headerPadding, collapsible = false, defaultCollapsed = false, onCollapseChange, onTitleClick, showHistory = false, onHistorySelect, maxHistoryCount = 50, }) => {
    // 收拢状态
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    // 历史记录状态
    const [historyList, setHistoryList] = useState([]);
    // 切换收拢状态
    const handleToggleCollapse = () => {
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed);
        onCollapseChange?.(newCollapsed);
    };
    // 保存历史记录
    const saveHistory = useCallback((data) => {
        if (!showHistory)
            return;
        try {
            // 将数据转换为 JSON 字符串进行存储
            const jsonData = typeof data === "string" ? data : JSON.stringify(data);
            setHistoryList((prev) => {
                // 避免重复添加相同的历史记录
                const newList = [
                    jsonData,
                    ...prev.filter((item) => item !== jsonData),
                ];
                // 限制历史记录数量
                return newList.slice(0, maxHistoryCount);
            });
        }
        catch (error) {
            console.error("保存历史记录失败:", error);
        }
    }, [showHistory, maxHistoryCount]);
    // 选择历史记录
    const handleHistorySelect = (value) => {
        try {
            // 尝试解析 JSON，如果失败则直接使用字符串
            let parsedData;
            try {
                parsedData = JSON.parse(value);
            }
            catch {
                parsedData = value;
            }
            onHistorySelect?.(parsedData);
        }
        catch (error) {
            console.error("解析历史记录失败:", error);
        }
    };
    // 历史记录上下文值
    const historyContextValue = useMemo(() => ({
        saveHistory,
    }), [saveHistory]);
    // 渲染按钮组
    const renderButtonGroup = (groupType) => {
        // 过滤出该分组的按钮
        const groupButtons = buttons.filter((button) => typeof button === "object" &&
            "group" in button &&
            button.group === groupType);
        if (groupButtons.length === 0)
            return null;
        return (_jsx(Space, { children: groupButtons.map((button) => {
                if (typeof button === "object" &&
                    "hidden" in button &&
                    button.hidden) {
                    return null;
                }
                if (typeof button === "object" && "key" in button) {
                    const buttonType = button.type === "danger" ? "default" : button.type;
                    const buttonContent = (_jsx(Button, { type: buttonType, danger: button.type === "danger" || button.danger, icon: button.icon, disabled: button.disabled, loading: button.loading, onClick: button.onClick, size: "small", style: button.style, children: button.showTextOnHover ? null : button.text }));
                    // 如果设置了 showTextOnHover 或 title，使用 Tooltip 包裹
                    if (button.showTextOnHover || button.title) {
                        const tooltipTitle = button.title || button.text || "";
                        return (_jsx(Tooltip, { title: tooltipTitle, children: buttonContent }, button.key));
                    }
                    return (_jsx(React.Fragment, { children: buttonContent }, button.key));
                }
                return null;
            }) }));
    };
    // 渲染历史记录下拉框
    const renderHistorySelect = () => {
        if (!showHistory || historyList.length === 0) {
            return null;
        }
        return (_jsx(Select, { placeholder: "\u5386\u53F2\u8BB0\u5F55", style: { width: 150, marginRight: 8 }, size: "small", value: undefined, onChange: handleHistorySelect, options: historyList.map((item, index) => {
                // 尝试解析 JSON 以生成更友好的显示文本
                let displayText;
                try {
                    const parsed = JSON.parse(item);
                    // 如果解析成功，尝试生成简短描述
                    if (typeof parsed === "object" && parsed !== null) {
                        displayText = `记录 ${index + 1}`;
                    }
                    else {
                        displayText = String(parsed).substring(0, 20);
                    }
                }
                catch {
                    displayText = String(item).substring(0, 20);
                }
                return {
                    label: displayText,
                    value: item,
                };
            }) }));
    };
    // 渲染所有按钮
    const renderButtons = () => {
        // 分离 ButtonConfig 和 ReactNode
        const buttonConfigs = [];
        const reactNodes = [];
        // 如果有传入的按钮，处理它们
        if (Array.isArray(buttons) && buttons.length > 0) {
            buttons.forEach((btn) => {
                if (React.isValidElement(btn)) {
                    reactNodes.push(btn);
                }
                else if (typeof btn === "object" && btn !== null) {
                    buttonConfigs.push(btn);
                }
            });
        }
        // 如果有收拢功能，将收拢按钮添加到 secondary 组（第二组，在 primary 组左侧）
        if (collapsible) {
            buttonConfigs.push({
                key: "collapse",
                icon: collapsed ? _jsx(DownOutlined, {}) : _jsx(UpOutlined, {}),
                type: "text",
                group: "secondary",
                onClick: handleToggleCollapse,
                title: collapsed ? "展开" : "收拢",
                showTextOnHover: true,
                style: {
                    padding: 0,
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                },
            });
        }
        // 如果没有按钮且没有收拢功能，返回 null
        if (buttonConfigs.length === 0 && reactNodes.length === 0 && !collapsible) {
            return null;
        }
        // 如果只有 React 节点，直接渲染
        if (buttonConfigs.length === 0 && reactNodes.length > 0) {
            return (_jsx("div", { style: {
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    justifyContent: "flex-end",
                }, children: _jsx(Space, { children: reactNodes }) }));
        }
        // 处理按钮配置
        const primaryButtons = buttonConfigs.filter((btn) => "group" in btn && btn.group === "primary");
        const secondaryButtons = buttonConfigs.filter((btn) => "group" in btn && btn.group === "secondary");
        const tertiaryButtons = buttonConfigs.filter((btn) => "group" in btn && btn.group === "tertiary");
        const ungroupedButtons = buttonConfigs.filter((btn) => !("group" in btn) || !btn.group);
        // 判断是否有左侧按钮（tertiary）
        const hasTertiaryButtons = tertiaryButtons.length > 0;
        // 判断是否有右侧按钮组（primary 或 secondary）
        const hasRightButtonGroups = primaryButtons.length > 0 || secondaryButtons.length > 0;
        // 判断是否需要靠右对齐：如果没有左侧按钮，只有右侧按钮或未分组按钮，则整体靠右
        const shouldAlignRight = !hasTertiaryButtons &&
            (hasRightButtonGroups ||
                ungroupedButtons.length > 0 ||
                reactNodes.length > 0);
        return (_jsxs("div", { style: {
                display: "flex",
                alignItems: "center",
                gap: "16px",
                justifyContent: shouldAlignRight ? "flex-end" : "flex-start",
            }, children: [extraLeading, renderHistorySelect(), tertiaryButtons.length > 0 && renderButtonGroup("tertiary"), (primaryButtons.length > 0 || secondaryButtons.length > 0) && (_jsxs("div", { style: {
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        marginLeft: hasTertiaryButtons ? "auto" : undefined,
                    }, children: [secondaryButtons.length > 0 && renderButtonGroup("secondary"), primaryButtons.length > 0 && renderButtonGroup("primary")] })), ungroupedButtons.length > 0 && (_jsx(Space, { children: ungroupedButtons.map((button) => {
                        if (typeof button === "object" &&
                            "hidden" in button &&
                            button.hidden) {
                            return null;
                        }
                        if (typeof button === "object" && "key" in button) {
                            const buttonType = button.type === "danger" ? "default" : button.type;
                            const buttonContent = (_jsx(Button, { type: buttonType, danger: button.type === "danger", icon: button.icon, disabled: button.disabled, onClick: button.onClick, size: "small", style: button.style, children: button.showTextOnHover ? null : button.text }));
                            // 如果设置了 showTextOnHover 或 title，使用 Tooltip 包裹
                            if (button.showTextOnHover || button.title) {
                                const tooltipTitle = button.title || button.text || "";
                                return (_jsx(Tooltip, { title: tooltipTitle, children: buttonContent }, button.key));
                            }
                            return (_jsx(React.Fragment, { children: buttonContent }, button.key));
                        }
                        return null;
                    }) })), reactNodes.length > 0 && _jsx(Space, { children: reactNodes })] }));
    };
    // 处理标题点击事件
    const renderTitle = () => {
        if (title && onTitleClick) {
            const handleKeyDown = (e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onTitleClick();
                }
            };
            return (_jsx("button", { type: "button", onClick: onTitleClick, onKeyDown: handleKeyDown, style: {
                    cursor: "pointer",
                    userSelect: "none",
                    background: "none",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    font: "inherit",
                    color: "inherit",
                    textAlign: "inherit",
                }, children: title }));
        }
        return title;
    };
    const cardTabList = tabs?.map((item) => ({
        key: String(item.key),
        label: item.label,
    }));
    // tabs 模式下，内容区渲染当前选中 tab 的 children；若 tab 无 children 则使用传入的 children
    const bodyContent = tabs
        ? (tabs.find((t) => t.key === activeTabKey)?.children ?? children)
        : children;
    let cardHeight;
    if (height === undefined) {
        cardHeight = "100%";
    }
    else if (height === "auto") {
        cardHeight = "auto";
    }
    else if (typeof height === "number") {
        cardHeight = `${height}px`;
    }
    else {
        cardHeight = height;
    }
    // 构建 Card 样式
    const cardStyle = {
        display: "flex",
        flexDirection: "column",
        border: bordered ? COMPONENT_OUTLINE_BORDER : "none",
        ...(shadow && {
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
        }),
        ...cardProps.style,
        height: cardHeight,
    };
    // 构建 Card body 样式：height="auto" 时随内容自适应；有固定 height 时限制 body 不溢出；否则 flex 填满
    const isAutoHeight = height === "auto";
    const bodyStyle = {
        padding: typeof contentPadding === "number"
            ? `${contentPadding}px`
            : contentPadding,
        ...(isAutoHeight
            ? { height: "auto", minHeight: "auto" }
            : { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }),
        ...(height !== undefined && height !== "auto" && { overflow: "hidden" }),
        ...cardProps.styles?.body,
    };
    // 构建 Card header 样式（标题栏下划线最后设置，避免被 cardProps 覆盖）
    const headerStyle = {
        ...(headerPadding !== undefined ? { padding: headerPadding } : undefined),
        ...cardProps.styles?.header,
        borderBottom: COMPONENT_OUTLINE_BORDER,
    };
    const panelClassName = [
        cardProps.className,
        tabs ? "container-panel-with-header-tabs" : "",
    ]
        .filter(Boolean)
        .join(" ");
    return (_jsxs(_Fragment, { children: [tabs ? (_jsx("style", { children: `
            .container-panel-with-header-tabs.ant-card-contain-tabs > .ant-card-head {
              padding: 0 16px;
            }
            .container-panel-with-header-tabs .ant-card-head-wrapper:empty {
              display: none;
            }
            .container-panel-with-header-tabs .ant-card-head-tabs .ant-tabs-nav {
              margin-bottom: 0;
            }
            .container-panel-with-header-tabs .ant-card-head-tabs .ant-tabs-nav::before {
              display: none;
            }
          ` })) : null, _jsx(HistoryContext.Provider, { value: historyContextValue, children: _jsx(Card, { ...cardProps, className: panelClassName || undefined, title: tabs ? undefined : renderTitle(), extra: tabs ? undefined : renderButtons(), tabList: cardTabList, activeTabKey: tabs ? activeTabKey : undefined, onTabChange: tabs ? onTabChange : undefined, tabBarExtraContent: tabs ? renderButtons() : undefined, tabProps: tabs
                        ? {
                            tabBarGutter: 32,
                            indicator: { size: 2 },
                            ...cardProps.tabProps,
                        }
                        : cardProps.tabProps, style: cardStyle, styles: {
                        ...cardProps.styles,
                        body: bodyStyle,
                        header: headerStyle,
                    }, children: !collapsed && bodyContent }) })] }));
};
export default ContainerPanel;
